import { ReactNode } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Form } from "@/components/ui/form";
import {
  FieldValues,
  useForm,
  UseFormProps,
  UseFormReturn,
} from "react-hook-form";
import { cn } from "@/util/cn";
import { ZodTypeAny } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

type FormCardProps<T extends FieldValues, Schema extends ZodTypeAny> = {
  title?: string;
  description?: ReactNode;
  showAccentBar?: boolean;
  options?: UseFormProps<T>;
  schema: Schema;
  children: (form: UseFormReturn<T>) => ReactNode;
  className?: string;
  cardClassName?: string;
  onSubmit: (data: T) => void;
};

export function FormCard2<T extends FieldValues, Schema extends ZodTypeAny>({
  title,
  description,
  showAccentBar = false,
  schema,
  options,
  onSubmit,
  children,
  className,
  cardClassName,
}: FormCardProps<T, Schema>) {
  const form = useForm({
    resolver: zodResolver(schema),
    ...options,
  });

  return (
    <div className={cn("w-full max-w-lg mx-auto", className)}>
      <Card
        className={cn(
          "bg-transparent border-transparent shadow-none",
          cardClassName
        )}
      >
        <CardHeader>
          {showAccentBar && (
            <div className="w-full h-1 bg-brand-neutral-700 rounded-full mb-4 overflow-hidden">
              <div className="w-24 h-full bg-brand-orange-500 rounded-full" />
            </div>
          )}
          {title && (
            <CardTitle className="text-2xl tracking-tight">{title}</CardTitle>
          )}
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="flex flex-col gap-6">{children(form)}</div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
